import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api, getToken, setSessionExpiredHandler, setToken } from "../api";

// The signed-in user (GET /api/auth/me -> UserOut) and how to get one.
//
// Two ways in, both ending in the portal's own JWT:
//   - Keycloak: the backend runs the code flow and hands the token to /login/complete in the
//     URL fragment, which calls loginWithToken(). The SPA never sees a Keycloak token.
//   - Password (a local account, while password sign-in is on): login(email, password)
//     posts {email, password} to /api/auth/login. The e-mail is the sign-in identifier (N5);
//     only the bootstrap admin types its name (ADMIN_INIT_USERNAME, e.g. "admin") instead.
// Keycloak only proves who somebody is; permissions are given in Management, project
// memberships by each project's curators.
//
// Sign-out tells the backend first (POST /auth/logout marks a Keycloak browser so its next
// sign-in asks for the password again), then forgets the token. A Keycloak session (amr
// "keycloak") then goes through the backend to Keycloak's end-session endpoint so the SSO
// session ends too; logout() resolves to true when it has sent the browser there, false when
// the caller should show /login itself.
//
// Only a 401 signs the user out. A failed GET /auth/me for any other reason (the browser aborting
// the fetch because the user navigated or reloaded, a timeout, a 502 while the backend restarts)
// keeps the token: the start-up check retries once after RETRY_MS, then sets `authError`, and
// ProtectedRoute shows "Could not reach the server" with a Try again button (retryAuth) instead
// of the sign-in page. loginWithToken() keeps the new token on such an error and rethrows.
//
// useAuth() -> { user, loading, authError, retryAuth(), login(email, password),
//                loginWithToken(token), logout(), updateUser(me) }
//   updateUser(me): the MeOut a PATCH /api/me or POST /api/me/profile/submit answered.
//   user: MeOut = { id, username, email, first_name, last_name, full_name, institute, address,
//                   orcid, profile_state: "incomplete"|"submitted"|"confirmed",
//                   missing_profile_fields[] ("email"|"first_name"|"last_name"),
//                   roles[] ("admin"|"curator"),
//                   memberships[{project_id, project_slug, project_name, role: "curator"|"user"}],
//                   is_admin, is_curator, auth_source: "local"|"keycloak", amr: "local"|"keycloak" }
//   An account whose profile_state is not "confirmed" (a Keycloak self-registration) reaches only
//   its own profile: ProtectedRoute shows ProfileGatePage instead of any page (pages/ProfilePage.jsx).

const AuthContext = createContext(null);

/** May this account use the portal? False while its profile waits (incomplete or submitted). */
export function profileConfirmed(user) {
  return !!user && (!user.profile_state || user.profile_state === "confirmed");
}
const RETRY_MS = 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const live = useRef(true);

  // GET /auth/me, retried once unless the server answered 401 (api.js has then already dropped
  // the token and called the session-expired handler).
  const loadMe = useCallback(async () => {
    setLoading(true);
    setAuthError(null);
    for (let attempt = 0; ; attempt += 1) {
      try {
        const u = await api.get("/auth/me");
        if (live.current) setUser(u);
        break;
      } catch (e) {
        if (e.status === 401) { setToken(null); break; }
        if (attempt >= 1) { if (live.current) setAuthError(e); break; }
        await new Promise(r => setTimeout(r, RETRY_MS));
        if (!live.current || !getToken()) break;
      }
    }
    if (live.current) setLoading(false);
  }, []);

  useEffect(() => {
    live.current = true;
    setSessionExpiredHandler(() => setUser(null));
    if (!getToken()) setLoading(false);
    else loadMe();
    return () => { live.current = false; };
  }, [loadMe]);

  const loginWithToken = useCallback(async token => {
    setToken(token);
    try {
      const u = await api.get("/auth/me");
      setUser(u);
      setAuthError(null);
      return u;
    } catch (e) {
      if (e.status === 401) setToken(null);
      throw e;
    }
  }, []);

  const login = useCallback(async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    return loginWithToken(r.access_token);
  }, [loginWithToken]);

  const logout = useCallback(async () => {
    const throughKeycloak = !user || user.amr !== "local";
    try { await api.post("/auth/logout"); } catch { /* the token is forgotten either way */ }
    setToken(null);
    setUser(null);
    setAuthError(null);
    if (throughKeycloak) {
      window.location.replace("/api/auth/oidc/logout");
      return true;
    }
    return false;
  }, [user]);

  const updateUser = useCallback(me => setUser(me), []);

  const value = useMemo(() => ({ user, loading, authError, retryAuth: loadMe, login, loginWithToken, logout,
                                 updateUser }),
                        [user, loading, authError, loadMe, login, loginWithToken, logout, updateUser]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
